import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TimeStatsService,
  TimeStatsItem,
  TimeStatsParams,
} from '../../core/services/time-stats.service';
import { ResultService, ResultItem } from '../../core/services/result.service';
import { CHART_COLOR_FALLBACK, CHART_COLOR_PALETTE } from '../../shared/constants/chart-palette';
import moment from 'moment';

interface ChartSeriesPoint {
  name: string;
  value: number;
}

interface ChartSeries {
  name: string;
  series: ChartSeriesPoint[];
}

@Component({
  selector: 'app-ng-time-statistics-report',
  template: require('./ng-time-statistics-report.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-time-statistics-report.component.scss').default],
  standalone: false,
})
export class NgTimeStatisticsReportComponent implements OnInit {
  // Date picker
  dateFrom: moment.Moment;
  dateTo: moment.Moment;

  // Charts Data
  timprisReport: ChartSeries[] = [];
  occupationReport: ChartSeries[] = [];
  hoursReport: ChartSeries[] = [];
  resultReport: ChartSeries[] = [];

  // Full Data (for filtering)
  fullTimprisReport: ChartSeries[] = [];
  fullOccupationReport: ChartSeries[] = [];
  fullHoursReport: ChartSeries[] = [];
  fullResultReport: ChartSeries[] = [];

  // Hidden Series
  hiddenSeries = {
    timpris: new Set<string>(),
    occupation: new Set<string>(),
    hours: new Set<string>(),
    result: new Set<string>(),
  };

  // Series Definitions for Custom Legend
  seriesDefinitions = {
    timpris: ['Timpris', 'Timpris (utan 0kr)'],
    occupation: ['Tillgängliga timmar', 'Utan OH/Tjl'],
    hours: [
      'Debiterbart',
      'Idle',
      'Intern/kompetens',
      'Intern projekt',
      'Sjukdom',
      'OH',
      'Tjänstledigt',
      'Semester',
    ],
    result: ['Omsättning', 'Resultat', 'Prognos Oms', 'Prognos Res'],
  };

  // Chart Settings
  colorScheme = {
    domain: [...CHART_COLOR_PALETTE],
  };

  // Color Mapping
  private colorMap = new Map<string, string>();
  customColors = (name: string): string => {
    return this.colorMap.get(name) || CHART_COLOR_FALLBACK;
  };

  legendPosition = 'right';
  showXAxis = true;
  showYAxis = true;
  showLegend = false; // Disable native legend
  showXAxisLabel = true;
  showYAxisLabel = true;
  xAxisLabel = 'Månad';

  // Y Axis Labels
  yAxisLabelTimpris = 'Timpris';
  yAxisLabelOccupation = '%';
  yAxisLabelHours = 'Timmar';
  yAxisLabelResult = 'K SEK';

  // Chart Options
  hoursStacked = false;
  barPaddingGrouped = 0; // No space between bars within a group
  groupPaddingGrouped = 10; // Space between groups
  barPaddingStacked = 20; // Space between stacked bars

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(TimeStatsService) private timeStatsService: TimeStatsService,
    @Inject(ResultService) private resultService: ResultService
  ) {
    // Default dates logic from legacy
    const now = moment().startOf('day').startOf('month');
    this.dateTo = now.clone().add(1, 'month').subtract(1, 'days');
    this.dateFrom = this.dateTo.clone().add(1, 'days').subtract(12, 'month');

    // Initialize colors
    Object.values(this.seriesDefinitions)
      .flat()
      .forEach((name, index) => {
        if (!this.colorMap.has(name)) {
          this.colorMap.set(name, this.colorScheme.domain[index % this.colorScheme.domain.length]);
        }
      });
  }

  ngOnInit(): void {
    this.fetchSummary();
  }

  // Helper for template to get legend items
  getLegendItems(chartType: keyof typeof this.seriesDefinitions) {
    return this.seriesDefinitions[chartType].map((label) => ({
      label,
      color: this.customColors(label),
      hidden: this.hiddenSeries[chartType].has(label),
    }));
  }

  toggleSeries(chartType: keyof typeof this.seriesDefinitions, label: string): void {
    const hiddenSet = this.hiddenSeries[chartType];
    if (hiddenSet.has(label)) {
      hiddenSet.delete(label);
    } else {
      hiddenSet.add(label);
    }
    this.updateCharts();
  }

  fetchSummary(): void {
    const params: TimeStatsParams = {
      custMode: { external: true, internal: true, absense: true },
      cust: undefined,
      proj: undefined,
      act: undefined,
      per: 'A',
      type: 'K',
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.timeStatsService.get(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      // Sort data
      data.sort((a, b) => {
        const dateA = new Date(a.year, a.month - 1).getTime();
        const dateB = new Date(b.year, b.month - 1).getTime();
        return dateA - dateB;
      });

      // Temporary arrays
      const tempTimpris: ChartSeries[] = [];
      const tempOccupation: ChartSeries[] = [];
      const tempHours: ChartSeries[] = [];

      data.forEach((item) => {
        const dateLabel = moment(new Date(item.year, item.month - 1)).format('YYYY-MM');

        // Timpris
        tempTimpris.push({
          name: dateLabel,
          series: [
            { name: 'Timpris', value: item.timpris },
            { name: 'Timpris (utan 0kr)', value: item.timpris_utan_noll },
          ],
        });

        // Occupation
        const occ1 = item.tot ? (100 * item.debit) / item.tot : 0;
        const denominator2 = item.tot - item.oh - item.tjl - item.semester;
        const occ2 = denominator2 ? (100 * item.debit) / denominator2 : 0;

        tempOccupation.push({
          name: dateLabel,
          series: [
            { name: 'Tillgängliga timmar', value: occ1 },
            { name: 'Utan OH/Tjl', value: occ2 },
          ],
        });

        // Hours
        tempHours.push({
          name: dateLabel,
          series: [
            { name: 'Debiterbart', value: item.debit },
            { name: 'Idle', value: item.idle },
            { name: 'Intern/kompetens', value: item.int_komp },
            { name: 'Intern projekt', value: item.int_proj },
            { name: 'Sjukdom', value: item.sjuk },
            { name: 'OH', value: item.oh },
            { name: 'Tjänstledigt', value: item.tjl },
            { name: 'Semester', value: item.semester },
          ],
        });
      });

      this.fullTimprisReport = tempTimpris;
      this.fullOccupationReport = tempOccupation;
      this.fullHoursReport = tempHours;

      this.assignColors([...tempTimpris, ...tempOccupation, ...tempHours]); // Pre-assign colors
      this.updateCharts();
    });

    // Result Data
    this.resultService.get({ from: params.from, to: params.to }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const resultMap = new Map<string, { oms: number; res: number }>();

      // Pre-fill map with prognosis data (2016 hardcoded legacy)
      // Actually, mixing "Prognos" logic into the same bars?
      // Legacy had "Prognos Oms" and "Prognos Res" as separate series.
      // If they are separate series, they are just bars alongside the actuals?
      // "MultiBarChart" in nvd3 plots them as separate bars if they have different keys.
      // So for 2016-05, we might have Oms, Res, Prognos Oms, Prognos Res.
      // But Prognos is only for specific dates.

      // First, gather all dates from API data
      data.forEach((item) => {
        const dateLabel = moment(item.period).format('YYYY-MM');
        if (!resultMap.has(dateLabel)) {
          resultMap.set(dateLabel, { oms: 0, res: 0 });
        }
        const entry = resultMap.get(dateLabel)!;
        if (item.account_name === 'res') entry.res = item.value / 1000;
        if (item.account_name === 'oms') entry.oms = item.value / 1000;
      });

      // Convert map to array and sort
      const sortedDates = Array.from(resultMap.keys()).sort();
      const tempResult: ChartSeries[] = [];

      sortedDates.forEach((dateLabel) => {
        const entry = resultMap.get(dateLabel)!;
        const series = [
          { name: 'Omsättning', value: entry.oms },
          { name: 'Resultat', value: entry.res },
        ];

        // Add Prognos if matches (Legacy values scaled by 1000)
        if (dateLabel === '2016-05') {
          series.push({ name: 'Prognos Oms', value: 2000 });
          series.push({ name: 'Prognos Res', value: 300 });
        }
        if (dateLabel === '2016-06') {
          series.push({ name: 'Prognos Oms', value: 2100 });
          series.push({ name: 'Prognos Res', value: 340 });
        }

        tempResult.push({
          name: dateLabel,
          series: series,
        });
      });

      this.fullResultReport = tempResult;
      this.assignColors(tempResult);
      this.updateCharts();
    });
  }

  private assignColors(data: ChartSeries[]): void {
    let colorIndex = 0;
    data.forEach((group) => {
      group.series.forEach((item) => {
        if (!this.colorMap.has(item.name)) {
          this.colorMap.set(
            item.name,
            this.colorScheme.domain[colorIndex % this.colorScheme.domain.length]
          );
          colorIndex++;
        }
      });
    });
  }

  updateCharts(): void {
    console.log('Updating charts. Hidden:', JSON.stringify(this.hiddenSeries));
    this.timprisReport = this.filterSeries(this.fullTimprisReport, 'timpris');
    this.occupationReport = this.filterSeries(this.fullOccupationReport, 'occupation');
    this.hoursReport = this.filterSeries(this.fullHoursReport, 'hours');
    this.resultReport = this.filterSeries(this.fullResultReport, 'result');
    console.log('Timpris report filtered length:', this.timprisReport.length);
    if (this.timprisReport.length > 0) {
      console.log('First group series count:', this.timprisReport[0].series.length);
    }
  }

  private filterSeries(
    data: ChartSeries[],
    chartType: keyof typeof this.hiddenSeries
  ): ChartSeries[] {
    return data.map((group) => ({
      name: group.name,
      series: group.series.filter((item) => !this.hiddenSeries[chartType].has(item.name)),
    }));
  }

  onLegendLabelClick(chartType: string, label: string): void {
    console.log(`Legend clicked: type=${chartType}, label=${label}`);
    // ngx-charts emits the label string
    const hiddenSet = this.hiddenSeries[chartType as keyof typeof this.hiddenSeries];
    if (hiddenSet.has(label)) {
      hiddenSet.delete(label);
    } else {
      hiddenSet.add(label);
    }
    this.updateCharts();
  }

  private mapToSeries(map: Map<string, ChartSeriesPoint[]>): ChartSeries[] {
    const result: ChartSeries[] = [];
    map.forEach((points, name) => {
      result.push({ name, series: points });
    });
    return result;
  }

  onSelect(event: any): void {
    console.log(event);
  }
}
